import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface ContactSubjectOption {
  id: number;
  label: string;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface ContactSubjectManagerProps {
  onBackToDeveloper: () => void;
}

const emptyForm = {
  label: '',
  description: '',
  sortOrder: '100',
  isActive: true,
};

const extractErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const ContactSubjectManager: React.FC<ContactSubjectManagerProps> = ({ onBackToDeveloper }) => {
  const [subjectOptions, setSubjectOptions] = useState<ContactSubjectOption[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadSubjectOptions = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.adminContactSubjectOptions);
      setSubjectOptions(response.data.subjectOptions || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, '件名候補一覧の取得に失敗しました。'));
    }
  }, []);

  useEffect(() => {
    loadSubjectOptions();
  }, [loadSubjectOptions]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    const payload = {
      label: form.label.trim(),
      description: form.description.trim(),
      sortOrder: Number(form.sortOrder || '100'),
      isActive: form.isActive,
    };

    if (!payload.label) {
      setError('件名を入力してください。');
      return;
    }

    try {
      if (editingId) {
        await apiClient.patch(API_ENDPOINTS.adminContactSubjectOption(editingId), payload);
        setSuccessMessage('件名候補を更新しました。');
      } else {
        await apiClient.post(API_ENDPOINTS.adminContactSubjectOptions, payload);
        setSuccessMessage('件名候補を追加しました。');
      }
      resetForm();
      await loadSubjectOptions();
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, '件名候補の保存に失敗しました。'));
    }
  };

  const startEdit = (item: ContactSubjectOption) => {
    setEditingId(item.id);
    setForm({
      label: item.label,
      description: item.description || '',
      sortOrder: String(item.sortOrder),
      isActive: item.isActive,
    });
  };

  const handleToggleActive = async (item: ContactSubjectOption) => {
    try {
      await apiClient.patch(API_ENDPOINTS.adminContactSubjectOption(item.id), {
        isActive: !item.isActive,
      });
      setSuccessMessage('件名候補の状態を更新しました。');
      await loadSubjectOptions();
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, '件名候補の状態更新に失敗しました。'));
    }
  };

  return (
    <div className="login-activity-page">
      <section className="login-activity-hero">
        <div>
          <span className="login-activity-eyebrow">Admin Visibility</span>
          <h2>件名候補管理</h2>
          <p>お問い合わせ開始時にユーザーが選択する件名候補を管理します。</p>
        </div>
        <div className="login-activity-hero-actions">
          <button className="login-activity-back-button" onClick={onBackToDeveloper}>
            管理者画面へ戻る
          </button>
        </div>
      </section>

      {error && <div className="error-message"><span>{error}</span></div>}
      {successMessage && <div className="success-message"><span>{successMessage}</span></div>}

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>{editingId ? '件名候補を編集' : '件名候補を追加'}</h3>
            <p>並び順は小さい数字ほど上に表示されます。</p>
          </div>
        </div>

        <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 640 }}>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
            placeholder="件名"
            style={{ padding: '0.275rem 0.35rem' }}
          />
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="説明"
            style={{ padding: '0.275rem 0.35rem' }}
          />
          <input
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
            placeholder="並び順"
            style={{ padding: '0.275rem 0.35rem' }}
          />
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            有効
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleSubmit} style={{ padding: '0.275rem 0.425rem' }}>
              {editingId ? '更新する' : '追加する'}
            </button>
            {editingId && <button onClick={resetForm} style={{ padding: '0.275rem 0.425rem' }}>キャンセル</button>}
          </div>
        </div>
      </section>

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>登録済み件名候補</h3>
          </div>
        </div>

        <div className="login-activity-table-wrap">
          <table className="login-activity-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>件名</th>
                <th>説明</th>
                <th>並び順</th>
                <th>状態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {subjectOptions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="login-activity-empty-cell">件名候補はまだありません。</td>
                </tr>
              ) : (
                subjectOptions.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.label}</td>
                    <td>{item.description || '-'}</td>
                    <td>{item.sortOrder}</td>
                    <td>{item.isActive ? '有効' : '無効'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => startEdit(item)} style={{ padding: '0.275rem 0.425rem' }}>編集</button>
                        <button onClick={() => handleToggleActive(item)} style={{ padding: '0.275rem 0.425rem' }}>
                          {item.isActive ? '無効化' : '有効化'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ContactSubjectManager;
